import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Check, MapPin, Star, Building2, Leaf, Mountain } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

const featuredResorts = [
  {
    id: 1,
    name: 'Paradise Lake Resort',
    location: 'Udaipur, Rajasthan',
    type: 'Boutique Hotel',
    image: 'https://images.unsplash.com/photo-1683914791874-2dcb78e58e09?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwzfHxib3V0aXF1ZSUyMGhvdGVsfGVufDB8fHx8MTc2NDE1NTAxNHww&ixlib=rb-4.1.0&q=85',
    description: 'Luxury lakeside retreat with stunning palace views and royal hospitality',
    rating: 4.9
  },
  {
    id: 2,
    name: 'Royal Heritage Haveli',
    location: 'Jaipur, Rajasthan',
    type: 'Boutique Hotel',
    image: 'https://images.unsplash.com/photo-1694267900696-0d8b1cff6728?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHwyfHxJbmRpYSUyMHJlc29ydHxlbnwwfHx8fDE3NjQxNTUwMDh8MA&ixlib=rb-4.1.0&q=85',
    description: 'Traditional Rajasthani architecture with modern amenities',
    rating: 4.9
  },
  {
    id: 3,
    name: 'Coastal Paradise Resort',
    location: 'Goa',
    type: 'Beach Resort',
    image: 'https://images.unsplash.com/photo-1685423705981-f7149db95a86?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHwzfHxJbmRpYSUyMHJlc29ydHxlbnwwfHx8fDE3NjQxNTUwMDh8MA&ixlib=rb-4.1.0&q=85',
    description: 'Private beach access with infinity pool overlooking the ocean',
    rating: 4.7
  },
  {
    id: 4,
    name: 'Wilderness Lodge',
    location: 'Ranthambore, Rajasthan',
    type: 'Wildlife Resort',
    image: 'https://images.unsplash.com/photo-1649731000184-7ced04998f44?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwxfHxib3V0aXF1ZSUyMGhvdGVsfGVufDB8fHx8MTc2NDE1NTAxNHww&ixlib=rb-4.1.0&q=85',
    description: 'Experience wildlife safaris from this luxury lodge',
    rating: 4.9
  },
  {
    id: 5,
    name: 'Mountain Mist Homestay',
    location: 'Manali, Himachal Pradesh',
    type: 'Hill Station',
    image: 'https://images.unsplash.com/photo-1656533391146-dad89b5a8b40?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwyfHxib3V0aXF1ZSUyMGhvdGVsfGVufDB8fHx8MTc2NDE1NTAxNHww&ixlib=rb-4.1.0&q=85',
    description: 'Cozy mountain retreat with panoramic Himalayan views',
    rating: 4.6
  },
  {
    id: 6,
    name: 'Jungle Treehouse Retreat',
    location: 'Wayanad, Kerala',
    type: 'Experiential Stay',
    image: 'https://images.unsplash.com/photo-1671739961405-add4f0ece0c3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwyfHxlY28lMjBsb2RnZXxlbnwwfHx8fDE3NjQxNTUwMTh8MA&ixlib=rb-4.1.0&q=85',
    description: 'Unique treehouse experience in lush tropical forests',
    rating: 4.8
  }
];

function HomePage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section 
        className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8 min-h-screen flex items-center"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1629558161082-52a9bda6060f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHwyfHxyZXNvcnQlMjBuYXR1cmV8ZW58MHx8fHwxNzY0MTU0Mzc2fDA&ixlib=rb-4.1.0&q=85)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
        data-testid="hero-section"
      >
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 to-black/50"></div>
        <div className="relative max-w-7xl mx-auto text-center">
          <Badge className="mb-4 bg-emerald-600 text-white px-4 py-2 text-sm">The First Global Resort-to-Resort Networking Platform</Badge>
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-6" data-testid="hero-headline">
            Built for Resorts. Powered by Collaboration.
          </h1>
          <p className="text-xl md:text-2xl text-gray-200 mb-10 max-w-3xl mx-auto">
            A cross-promotion and discovery network helping resorts get more direct bookings without OTA commissions.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
            <Button 
              size="lg" 
              className="bg-emerald-600 hover:bg-emerald-700 text-lg px-8 py-6"
              onClick={() => navigate('/join')}
              data-testid="list-property-btn"
            >
              List Your Property
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <Button 
              size="lg" 
              variant="outline" 
              className="bg-white/10 backdrop-blur-sm text-white border-white hover:bg-white/20 text-lg px-8 py-6"
              onClick={() => navigate('/properties')}
              data-testid="explore-stays-btn"
            >
              Explore Stays
            </Button>
          </div>

          {/* 3 Main Benefits */}
          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            <Card className="bg-white/10 backdrop-blur-md border-white/20" data-testid="benefit-no-commission">
              <CardContent className="pt-6 pb-6 text-center">
                <div className="w-12 h-12 bg-emerald-600 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Check className="h-6 w-6 text-white" />
                </div>
                <h3 className="font-semibold text-white text-lg mb-1">No OTA Commissions</h3>
                <p className="text-gray-200 text-sm">Keep 100% of your revenue</p>
              </CardContent>
            </Card>
            <Card className="bg-white/10 backdrop-blur-md border-white/20" data-testid="benefit-network">
              <CardContent className="pt-6 pb-6 text-center">
                <div className="w-12 h-12 bg-emerald-600 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Building2 className="h-6 w-6 text-white" />
                </div>
                <h3 className="font-semibold text-white text-lg mb-1">Cross-Promotion Network</h3>
                <p className="text-gray-200 text-sm">Connect with 500+ resorts</p>
              </CardContent>
            </Card>
            <Card className="bg-white/10 backdrop-blur-md border-white/20" data-testid="benefit-bookings">
              <CardContent className="pt-6 pb-6 text-center">
                <div className="w-12 h-12 bg-emerald-600 rounded-full flex items-center justify-center mx-auto mb-3">
                  <ArrowRight className="h-6 w-6 text-white" />
                </div>
                <h3 className="font-semibold text-white text-lg mb-1">More Bookings</h3>
                <p className="text-gray-200 text-sm">45% average increase</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Featured Resorts Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white" data-testid="featured-resorts-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Featured Partner Resorts</h2>
            <p className="text-xl text-gray-600">Discover unique stays from our network of verified properties</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {featuredResorts.map((resort) => (
              <Card key={resort.id} className="overflow-hidden hover:shadow-xl transition-all cursor-pointer" data-testid={`resort-card-${resort.id}`}>
                <div className="h-48 overflow-hidden">
                  <img 
                    src={resort.image} 
                    alt={resort.name}
                    className="w-full h-full object-cover hover:scale-110 transition-transform duration-300"
                  />
                </div>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-2">
                    <Badge className="bg-emerald-100 text-emerald-800">{resort.type}</Badge>
                    <div className="flex items-center space-x-1">
                      <Star className="h-4 w-4 fill-amber-400 text-amber-400" />
                      <span className="font-semibold text-sm">{resort.rating}</span>
                    </div>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{resort.name}</h3>
                  <div className="flex items-center text-gray-600 mb-3">
                    <MapPin className="h-4 w-4 mr-1" />
                    <span className="text-sm">{resort.location}</span>
                  </div>
                  <p className="text-gray-600 text-sm mb-4">{resort.description}</p>
                  <Button variant="outline" className="w-full" onClick={() => navigate('/properties')}>
                    View Details
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-12">
            <Button 
              size="lg"
              onClick={() => navigate('/properties')}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="view-all-properties-btn"
            >
              View All Properties
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* Why Choose Travelodges */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50" data-testid="why-choose-section">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Why Partner with Us?</h2>
            <p className="text-xl text-gray-600">Join India's fastest-growing resort network</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Card className="text-center border-2 hover:border-emerald-500 transition-all" data-testid="stat-partners">
              <CardContent className="pt-8 pb-8">
                <div className="text-5xl font-bold text-emerald-600 mb-2">500+</div>
                <div className="text-gray-600 font-medium">Partner Resorts</div>
              </CardContent>
            </Card>
            <Card className="text-center border-2 hover:border-emerald-500 transition-all" data-testid="stat-growth">
              <CardContent className="pt-8 pb-8">
                <div className="text-5xl font-bold text-emerald-600 mb-2">45%</div>
                <div className="text-gray-600 font-medium">Average Booking Growth</div>
              </CardContent>
            </Card>
            <Card className="text-center border-2 hover:border-emerald-500 transition-all" data-testid="stat-states">
              <CardContent className="pt-8 pb-8">
                <div className="text-5xl font-bold text-emerald-600 mb-2">28</div>
                <div className="text-gray-600 font-medium">States Covered</div>
              </CardContent>
            </Card>
          </div>

          <div className="mt-12 text-center">
            <Button 
              size="lg"
              onClick={() => navigate('/for-resorts')}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              Learn More for Resort Owners
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-emerald-600 to-emerald-800" data-testid="cta-section">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to Grow Your Resort Business?
          </h2>
          <p className="text-xl text-emerald-100 mb-10">
            Join hundreds of successful resort owners. List your property today.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg" 
              className="bg-white text-emerald-600 hover:bg-gray-100 text-lg px-10 py-6"
              onClick={() => navigate('/join')}
              data-testid="cta-join-btn"
            >
              List Your Property
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <Button 
              size="lg" 
              variant="outline"
              className="bg-transparent border-2 border-white text-white hover:bg-white/10 text-lg px-10 py-6"
              onClick={() => navigate('/pricing')}
            >
              View Pricing
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}

export default HomePage;